import { Dispatch } from 'react';

import { transformDateFormat } from 'susy-common/date-utils';
import {
    IAdminUpdateProjectDeadlines,
    AcceptRejectSharedAssessment,
} from 'susy-common/models';

import { SuSyApi } from '../../api/susyApi';
import {
    IAdminChangeProjectDatesPayload,
    PostDispatch,
    ProjectModel,
    ProjectTimeline,
    User,
} from '../types';
import {
    INITIAL_PROJECT_STATE,
    InvitationStatus,
    ProjectStatus,
    UserRoles,
} from '../../utils';
import {
    IProjectFile,
    IProjectUploadFile,
} from '../../definitions/interfaces/projectFileTypes';
import { ProjectActionType } from '../actionTypes/project';
import { ProjectOverviewActionType } from '../actionTypes/projectOverview';
import type { RootState } from '../reducers';
import { removeAdminDashboardProject } from './AdminActions';
import { getAllArchiveProjects } from './archive/ArchivedProjectActions';

export const getProject =
    (projectId: string | null) => async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.get(`/project/${projectId}`);
        dispatch({
            type: ProjectActionType.GET_PROJECT,
            payload: transformDateFormat(response.data),
        });
    };

export const resetProject = () => (dispatch: Dispatch<PostDispatch>) => {
    dispatch({
        type: ProjectActionType.RESET_PROJECT,
        payload: { ...INITIAL_PROJECT_STATE },
    });
};

export const updateProjectOwnerAndTeamAdmin =
    (project: ProjectModel) => async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.put(`/project/admin/ownerAndTeam`, {
            project,
        });
        dispatch({
            type: ProjectActionType.SAVE_PROJECT,
            payload: response.data,
        });
    };

//This is for admin
export const updateProjectSupervisors =
    (
        id: string,
        firstSupervisor: User,
        secondSupervisors: User[],
        removedSupervisors: User[]
    ) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.put(`/project/supervisors`, {
            projectId: id,
            firstSupervisor,
            secondSupervisors,
            removedSupervisors,
        });
        dispatch({
            type: ProjectActionType.SAVE_PROJECT,
            payload: response.data,
        });
    };

export const updateProjectUserChanges =
    (
        id: string,
        title: string | null,
        researchAreaTitle: string | null,
        researchAreaOverview: string | null,
        literature: string[] | null
    ) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.put(`/project/user/changes`, {
            projectId: id,
            title,
            researchAreaTitle,
            researchAreaOverview,
            literature,
        });
        dispatch({
            type: ProjectActionType.SAVE_PROJECT,
            payload: response.data,
        });
    };
export const reviewProjectChangesApprovalResult =
    (id: string) => async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.put(`/project/user/changes/review`, {
            projectId: id,
        });
        dispatch({
            type: ProjectActionType.SAVE_PROJECT,
            payload: response.data,
        });
    };

export const acceptProjectUserChanges =
    (id: string) =>
    async (dispatch: Dispatch<PostDispatch>): Promise<void> => {
        const response = await SuSyApi.post(
            `/project/supervisor/acceptUserChanges`,
            {
                projectId: id,
            }
        );
        dispatch({
            type: ProjectActionType.SAVE_PROJECT,
            payload: response.data,
        });
    };
export const declineProjectUserChanges =
    (id: string, declineDescription: string) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.post(
            `/project/supervisor/declineUserChanges`,
            {
                projectId: id,
                declineDescription,
            }
        );
        dispatch({
            type: ProjectActionType.SAVE_PROJECT,
            payload: response.data,
        });
    };

export const updateProject =
    (
        project: ProjectModel,
        files: Array<IProjectUploadFile>,
        projectStatus: ProjectStatus
    ) =>
    async (dispatch: Dispatch<PostDispatch>, getState?: () => RootState) => {
        const formData = new FormData();

        for (const f of files) {
            formData.append(`${f.fileType}`, f.file, f.file.name);
        }

        formData.append(
            'projectData',
            JSON.stringify({
                project,
                projectStatus,
            })
        );

        const response = await SuSyApi.put(`/project`, formData);

        if (projectStatus === ProjectStatus.withdrawn) {
            dispatch({
                type: ProjectOverviewActionType.WITHDRAW_PROJECT,
                payload: response.data,
            });
            dispatch(
                removeAdminDashboardProject(response.data?._id ?? project._id)
            );

            if (
                getState?.().auth.currentUser?.userRole.key === UserRoles.admin
            ) {
                await getAllArchiveProjects()(dispatch);
            }
        } else {
            dispatch({
                type: ProjectActionType.SAVE_PROJECT,
                payload: response.data,
            });
        }

        return response.data;
    };

export const addFirstSupervisor =
    (projectId: string, supervisorId: string) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.put(`/project/addFirstSupervisor`, {
            projectId,
            supervisorId,
        });

        dispatch({
            type: ProjectActionType.SAVE_PROJECT,
            payload: response.data,
        });
    };

export const adminEditProjectFiles =
    (
        project: ProjectModel,
        existingFiles: IProjectFile[],
        newFiles: Array<IProjectUploadFile>
    ) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const formData = new FormData();

        for (const file of newFiles) {
            formData.append(file.fileType, file.file, file.file.name);
        }

        formData.append(
            'projectData',
            JSON.stringify({
                project,
                existingFiles,
            })
        );

        const response = await SuSyApi.put(
            `/project/admin/editFiles`,
            formData
        );

        dispatch({
            type: ProjectActionType.SAVE_PROJECT,
            payload: response.data,
        });
    };

/**
 * @summary Use only for uploading files, not when uploading files when updating project!
 */
export const uploadAdditionalAndPresentationFiles =
    (project: ProjectModel, files: Array<IProjectUploadFile>) =>
    async (dispatch: Dispatch<PostDispatch>): Promise<void> => {
        const formData = new FormData();

        for (const f of files) {
            formData.append(f.fileType, f.file, f.file.name);
        }

        formData.append(
            'projectData',
            JSON.stringify({
                project,
            })
        );

        const response = await SuSyApi.put(
            `/project/additionalFiles`,
            formData
        );
        dispatch({
            type: ProjectActionType.SAVE_PROJECT,
            payload: response.data,
        });
    };

export const createProject =
    (
        project: ProjectModel,
        files: Array<IProjectUploadFile>,
        timeline: ProjectTimeline | null,
        projectStatus: ProjectStatus
    ) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const formData = new FormData();

        for (const f of files) {
            formData.append(`${f.fileType}`, f.file, f.file.name);
        }

        formData.append(
            'projectData',
            JSON.stringify({
                project,
                timeline,
                projectStatus,
            })
        );

        const response = await SuSyApi.post(`/project`, formData);
        dispatch({
            type: ProjectActionType.SAVE_PROJECT,
            payload: response.data,
        });

        return response.data;
    };

export const AdminUpdateProjectStatus =
    (projectId: string | null, projectStatus: ProjectStatus, note: string) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.patch(`/project/admin/status`, {
            projectId,
            projectStatus,
            note,
        });
        dispatch({
            type: ProjectActionType.UPDATE_PROJECT_STATUS,
            payload: response.data,
        });
    };

export const adminUpdateProjectTitle =
    (projectId: string, newTitle: string) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.patch(`/project/admin/title`, {
            projectId,
            newTitle,
        });
        dispatch({
            type: ProjectActionType.UPDATE_PROJECT_TITLE,
            payload: response.data,
        });
    };

export const adminUpdateProjectDeadlines =
    (
        projectId: string,
        projectDeadlinesPayload: IAdminUpdateProjectDeadlines
    ) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.patch(`/project/admin/deadlines`, {
            projectId,
            deadlines: projectDeadlinesPayload,
        });
        dispatch({
            type: ProjectActionType.UPDATE_PROJECT_DEADLINES,
            payload: response.data,
        });
    };

export const adminUpdateProjectDates =
    (projectId: string, projectDatesPayload: IAdminChangeProjectDatesPayload) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.patch('/project/admin/dates', {
            projectId,
            datesPayload: projectDatesPayload,
        });

        dispatch({
            type: ProjectActionType.UPDATE_PROJECT_DATES,
            payload: response.data,
        });
    };

export const declineProjectRegistartion =
    (project: ProjectModel) => async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.patch(
            `/project-management/admin/${project._id}/declineProjectRegistration`,
            project
        );
        dispatch({
            type: ProjectActionType.ADMIN_ACCEPT_REJECT_PROJECT_REGISTRATION,
            payload: response.data,
        });
    };
export const approveProjectRegistration =
    (project: ProjectModel) => async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.patch(
            `/project-management/admin/${project._id}/approveProjectRegistration`,
            project
        );
        dispatch({
            type: ProjectActionType.ADMIN_ACCEPT_REJECT_PROJECT_REGISTRATION,
            payload: response.data,
        });
    };

export const adminApproveProject =
    (projectId: string | null, comment: string) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.patch(
            `/project-management/admin/${projectId}/approveProject`,
            { approveComment: comment }
        );
        dispatch({
            type: ProjectActionType.ADMIN_APPROVE_DECLINE_PROJECT,
            payload: response.data,
        });
    };
export const adminDeclineProject =
    (projectId: string | null, declineDescription: string) =>
    async (dispatch: Dispatch<PostDispatch>): Promise<void> => {
        const response = await SuSyApi.patch(
            `/project-management/admin/${projectId}/declineProject`,
            { declineDescription: declineDescription }
        );
        dispatch({
            type: ProjectActionType.ADMIN_APPROVE_DECLINE_PROJECT,
            payload: response.data,
        });
    };

export const setGroupInvitationStatus =
    (projectId: string, invitationStatus: InvitationStatus) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.post(
            `/project/${projectId}/setGroupInvitationStatus`,
            { invitationStatus }
        );
        dispatch({
            type: ProjectActionType.DECLINE_GROUP_INVITATION,
            payload: response.data,
        });
    };

//This is for user
export const updateSecondSupervisors =
    (projectId: string, secondSupervisorIds: Array<string>) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.patch(
            `/project/updateSecondSupervisors`,
            {
                projectId,
                secondSupervisorIds,
            }
        );
        dispatch({
            type: ProjectActionType.UPDATE_SECOND_SUPERVISORS,
            payload: response.data,
        });
    };

export const addAdditionalSupervisor =
    (projectId: string | null, supervisorId: string | null) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.put(`/project/addAdditionalSupervisor`, {
            projectId,
            supervisorId,
        });
        dispatch({
            type: ProjectActionType.ADD_ADDITIONAL_SUPERVISOR,
            payload: response.data,
        });
    };

export const handleUserSeenSecondSupervisorDecline =
    (secondSupervisorProposalId: string | null) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.put(
            `/project/handleUserSeenSecondSupervisorDecline/${secondSupervisorProposalId}`
        );
        dispatch({
            type: ProjectActionType.SEEN_SECOND_SUPERVISOR,
            payload: response.data,
        });
    };

export const handleUserSeenAdminProjectRegistrationDecline =
    (project: ProjectModel) => async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.put(
            `/project/handleUserSeenAdminProjectRegistrationDecline/${project._id}`
        );
        dispatch({
            type: ProjectActionType.SEEN_ADMIN_DECLINE_PROJECT_REGISTRATION,
            payload: response.data,
        });
    };

export const getProjects =
    (userId: string | null) => async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.get(`/project/${userId}/projects`);
        dispatch({
            type: ProjectOverviewActionType.GET_INVOLVED_PROJECTS,
            payload: response.data,
        });
    };

export const acceptGrade =
    (projectId: string | null, selectedStudent: User | null) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.put(`/grading/acceptGrade`, {
            projectId,
            studentId: selectedStudent?._id,
        });
        dispatch({
            type: ProjectActionType.ACCEPT_GRADE,
            payload: response.data,
        });
    };

export const disagreeOnGrade =
    (projectId: string | null, selectedStudent: User | null) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.put(`/grading/disagreeGrade`, {
            projectId,
            studentId: selectedStudent?._id,
        });
        dispatch({
            type: ProjectActionType.DISAGREE_GRADE,
            payload: response.data,
        });
    };

export const acceptSharedAssessment =
    (request: AcceptRejectSharedAssessment) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.put(
            `/grading/acceptSharedAssessment`,
            request
        );
        dispatch({
            type: ProjectActionType.ACCEPT_GRADE,
            payload: response.data,
        });
    };

export const rejectSharedAssessment =
    (request: AcceptRejectSharedAssessment) =>
    async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.put(
            `/grading/rejectSharedAssessment`,
            request
        );
        dispatch({
            type: ProjectActionType.DISAGREE_GRADE,
            payload: response.data,
        });
    };

export const completeGrading =
    (projectId: string | null) => async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.patch(
            `/grading/${projectId}/completeGrading`
        );
        dispatch({
            type: ProjectActionType.COMPLETE_GRADING,
            payload: response.data,
        });
    };

export const completeGradingWithSharedAssessment =
    (projectId: string | null) => async (dispatch: Dispatch<PostDispatch>) => {
        const response = await SuSyApi.patch(
            `/grading/${projectId}/completeGradingWithSharedAssessment`
        );
        dispatch({
            type: ProjectActionType.COMPLETE_GRADING,
            payload: response.data,
        });
    };

export const adminUnlockProjectGrading =
    (projectId: string | null, supervisorIds: string[]) =>
    async (dispatch: Dispatch<PostDispatch>): Promise<void> => {
        const response = await SuSyApi.patch(
            `/grading/${projectId}/unlockGrading`,
            {
                projectId,
                supervisorIds,
            }
        );
        dispatch({
            type: ProjectActionType.UNLOCK_PROJECT_GRADING,
            payload: response.data,
        });
    };

export const adminResetProjectGrading =
    (projectId: string | null, supervisorIds: string[]) =>
    async (dispatch: Dispatch<PostDispatch>): Promise<void> => {
        const response = await SuSyApi.patch(
            `/grading/${projectId}/resetGrading`,
            {
                projectId,
                supervisorIds,
            }
        );
        dispatch({
            type: ProjectActionType.RESET_PROJECT_GRADING,
            payload: response.data,
        });
    };

export const adminToggleFinalReportAvailability =
    (projectId: string | null) =>
    async (dispatch: Dispatch<PostDispatch>): Promise<void> => {
        const response = await SuSyApi.patch(
            `/grading/${projectId}/toggleFinalReportAvailability`,
            {
                projectId,
            }
        );
        dispatch({
            type: ProjectActionType.TOGGLE_FINAL_REPORT_AVAILABILITY,
            payload: response.data,
        });
    };

export const adminToggleFailedStateForProject =
    (projectId: string | null, failureReasonDescription: string) =>
    async (dispatch: Dispatch<PostDispatch>): Promise<void> => {
        const response = await SuSyApi.patch(
            `/project-management/admin/${projectId}/setAsFailed`,
            { failureReasonDescription: failureReasonDescription }
        );
        dispatch({
            type: ProjectActionType.ADMIN_SET_AS_FAILED,
            payload: response.data,
        });
    };

export const adminRevertFailedStateForProject =
    (projectId: string | null) =>
    async (dispatch: Dispatch<PostDispatch>): Promise<void> => {
        const response = await SuSyApi.patch(
            `/project-management/admin/${projectId}/revertFailedStatus`
        );
        dispatch({
            type: ProjectActionType.ADMIN_SET_AS_NOT_FAILED,
            payload: response.data,
        });
    };
